import { Checkbox, Flex, Form, Input, InputNumber } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Form as CustomForm } from 'components/form/Form';
import { Subtitle, Title } from 'components/mvp-typography';
import { styles } from 'features/sign-in/v2/styles';
import { useOrganisationRegistrationMutation } from 'lib/hooks/tanstack/useOrganisationRegistrationMutation';
import { useEffect } from 'react';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { FormQuestion } from 'lib/types/form';
import { useErrorList } from 'lib/utils/useErrorList';

interface OrganisationRegistrationFormPageProps {
  goToNextStep: () => void;
}

const businessDetailsQuestions: FormQuestion[] = [
  {
    name: 'name',
    label: 'Organisation Name',
    component: () => <Input placeholder="Organisation Name" />,
    rules: [{ required: true, message: 'Please input your organisation name.' }]
  },
  {
    name: 'contactEmail',
    label: 'Contact Email',
    component: () => <Input placeholder="Contact Email" />,
    rules: [
      { required: true, message: 'Please input your contact email.' },
      { type: 'email', message: 'Please enter a valid email.' }
    ]
  },
  {
    name: 'phoneNumber',
    label: 'Contact Phone Number',
    component: () => <Input placeholder="Contact Phone Number" />,
    rules: [
      {
        required: true,
        message: 'Please input your contact phone number.'
      }
    ]
  },
  {
    name: 'address',
    label: 'Organisation Address',
    component: () => <Input placeholder="Organisation Address" />,
    rules: [{ required: true, message: 'Please input your organisation address.' }]
  },
  {
    name: 'addressLine2',
    label: 'Organisation Address Line 2',
    component: () => <Input placeholder="Organisation Address Line 2" />
  }
];

const additionalDetailsQuestions: FormQuestion[] = [
  {
    name: ['organisationMetadata', 'userCount'],
    label: 'Estimated number of users',
    component: () => (
      <InputNumber
        min={1}
        style={{ width: '100%' }}
        placeholder="Estimated number of users"
      />
    ),
    rules: [
      {
        required: true,
        message: 'Please input estimated number of users.'
      }
    ]
  },
  {
    name: ['organisationMetadata', 'estimatedTestTakerCount'],
    label: 'Estimated number of test-takers per year',
    component: () => (
      <InputNumber
        min={0}
        style={{ width: '100%' }}
        placeholder="Estimated number of test-takers per year"
      />
    ),
    rules: [
      {
        required: true,
        message: 'Please input estimated number of test-takers.'
      }
    ]
  }
];

interface OrganisationFormFields {
  name: string;
  address: string;
  addressLine2: string | undefined;
  phoneNumber: string;

  contactEmail: string;
  organisationMetadata: {
    userCount: number;
    estimatedTestTakerCount: number;
  };
  __skipFormField_agreement: boolean;
}

export default function OrganisationRegistrationFormPage({
  goToNextStep
}: OrganisationRegistrationFormPageProps) {
  const [form] = Form.useForm<OrganisationFormFields>();
  const errorList = useErrorList();

  const { mutate, isLoading, error } = useOrganisationRegistrationMutation({
    onSuccessCallback: () => {
      goToNextStep();
    }
  });

  useQueryErrorNotifier({ error }, errorList);

  useEffect(() => {
    if (errorList.hasErrors) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errorList.hasErrors]);

  async function handleSubmit(formValues: OrganisationFormFields) {
    errorList.reset();
    console.debug(formValues)
    mutate({
      name: formValues.name,
      address: formValues.address,
      ...(formValues.addressLine2 !== undefined
        ? { addressLine2: formValues.addressLine2 }
        : {}),
      contactEmail: formValues.contactEmail,
      phoneNumber: formValues.phoneNumber,
      organisationMetadata: formValues.organisationMetadata
    });
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        gap: 16
      }}
    >
      {errorList.hasErrors && <ErrorListDisplay errorList={errorList} />}
      <Title>Create your Organization</Title>
      <CustomForm>
        <Form
          form={form}
          style={styles.form}
          layout="vertical"
          name="organisation_registration_form"
          onFinish={handleSubmit}
        >
          <Flex vertical gap={16}>
            <Flex vertical gap={16}>
              <Subtitle>Organization Details</Subtitle>
              {businessDetailsQuestions.map(question => (
                <Form.Item
                  key={
                    Array.isArray(question.name)
                      ? question.name.join('.')
                      : question.name
                  }
                  name={question.name}
                  label={question.label}
                  rules={question.rules}
                  extra={question.extra}
                >
                  {question.component()}
                </Form.Item>
              ))}
            </Flex>
            <Flex vertical gap={16}>
              <h3>Additional Details</h3>
              {additionalDetailsQuestions.map(question => (
                <Form.Item
                  key={
                    Array.isArray(question.name)
                      ? question.name.join('.')
                      : question.name
                  }
                  name={question.name}
                  label={question.label}
                  rules={question.rules}
                  extra={question.extra}
                >
                  {question.component()}
                </Form.Item>
              ))}
            </Flex>
            <Form.Item
              name="__skipFormField_agreement"
              valuePropName="checked"
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            'Please confirm the information is correct.'
                          )
                        )
                }
              ]}
            >
              <Checkbox>
                By clicking this checkbox, you are confirming that this
                information is correct and you are an authorized representative
                of the organisation.
              </Checkbox>
            </Form.Item>

            <div style={{ marginTop: '20px' }}>
              <BaseButton
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isLoading}
              >
                Next
              </BaseButton>
            </div>
          </Flex>
        </Form>
      </CustomForm>
    </div>
  );
}
